import { transition } from '@helpers/animate';
import borderRadius from '@helpers/borderRadius';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid, siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import { contentStyles, displayStyles, linkOpposite } from '@helpers/typography';
import SVGTrustPilotMobileNavTray from '@img/logos/trustpilot-mobile-nav-tray.svg';
import SVGTrustPilotTopHeaderMobile from '@img/logos/trustpilot-top-header-mobile.svg';
import { LinkButton } from '@stories/Components/Buttons/Button/Button';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled.header `
  ${transition('background-color')};

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${Layers.Header};

  background-color: ${brand.global.white};

  &::after {
    opacity: 1;
  }

  @media ${from(Device.TabletLarge)} {
    &::after {
      ${transition('opacity')};

      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      opacity: 0;
      box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.12);
    }
  }

  

  &[data-shrink-header='true'],
  &:hover {
    background-color: ${brand.global.white};

    &::after {
      opacity: 1;
    }
  }
`;
const Inner = styled.div `
  ${siteWide};

  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  padding-top: ${fluid(12, 24)};
  padding-bottom: ${fluid(12, 24)};

  @media ${from(Device.DesktopSmall)} {
    padding-top: ${fluid(12, 32)};
    padding-bottom: ${fluid(12, 32)};
  }
`;
const LogoWrapper = styled.a `
  position: relative;
  display: block;
  width: ${fluid(121, 202)};
  height: ${fluid(24, 40)};
  margin-right: 36px;

  @media ${until(Device.DesktopLarge)} {
    margin-right: 16px;
  }
`;
const logoStyles = css `
  ${transition('opacity')};

  display: block;
  height: 100%;
  object-fit: contain;
`;
const LogoAlt = styled.img `
  ${logoStyles};
  opacity: 1;

  @media ${from(Device.DesktopLarge)} {
    position: absolute;
    top: 0;
    left: 0;
  }
`;
const CallUs = styled.div `
  ${displayStyles};
  margin: 0;
`;
const CallUsLabel = styled.span `
  color: ${brand.global.black};
  font-size: 14px;
  line-height: 16px;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const CallUsLink = styled.a `
  display: flex;
  align-items: center;
  column-gap: ${fluid(4, 8)};
  text-decoration: none;

  svg {
    position: relative;
    top: ${fluid(0, 2)};
    color: ${brand.orange.base};
    width: 22px;
    height: 22px;

    @media ${until(Device.DesktopSmall)} {
      width: 12px;
      height: 12px;
    }
  }
`;
const CallUsNumber = styled.span `
  color: ${brand.tealAccessibleLight._1000};
  font-size: ${fluid(20, 32)};
  line-height: ${fluid(26, 38)};

`;
const InnerRight = styled.div `
  display: flex;
  align-items: center;
  column-gap: 40px;
  margin-left: auto;

  @media ${until(Device.DesktopLarge)} {
    column-gap: 20px;
    flex-shrink: 0;
  }

  @media ${until(Device.DesktopSmall)} {
    column-gap: 12px;
  }
`;
const MobileMenuToggle = styled.button `
  ${ButtonReset};

  display: flex;
  align-items: center;
  justify-content: center;
  width: ${fluid(36, 42)};
  height: ${fluid(36, 42)};
  border: 2px solid ${brand.global.black};
  border-radius: ${borderRadius.round}px;
  color: ${brand.global.black};

  @media ${from(Device.TabletLarge)} {
    display: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
const TopBarMobile = styled.div `
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px var(--sitePadding);
  background-color: ${brand.global.black};
  z-index: 1;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const TrustPilotLogoTopHeaderMobile = styled(SVGTrustPilotTopHeaderMobile) ``;
const HeaderCta = styled(LinkButton) `
  --colorContrast: ${brand.global.black};

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const DesktopNav = styled.nav `
  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const DesktopNavInner = styled.ul `
  display: flex;
`;
const DesktopNavItem = styled.li ``;
const desktopTopLevelNavItemStyles = css `
  ${fonts.aptos.extraBold};

  ${transition('color, border-color')};

  display: flex;
  align-items: center;
  column-gap: 8px;
  padding: 10px 20px;
  color: ${brand.global.black};
  font-size: 16px;
  line-height: 20px;
  border: 2px solid transparent;
  border-radius: ${borderRadius.round}px;
  text-decoration: none;

  svg {
    width: 10px;
    height: 6px;
    color: ${brand.orange._400};
  }

  ${Container}[data-shrink-header='true'] &,
  ${Container}:hover & {
    color: ${brand.global.black};
  }

  &:hover,
  ${DesktopNavItem}[data-is-active='true'] & {
    border-color: ${brand.orange._400};
  }

  ${DesktopNavItem}[data-is-active='true'] & {
    svg {
      transform: scaleY(-1);
    }
  }

  @media ${until(Device.DesktopLarge)} {
    padding: 5px 10px;
    color: ${brand.global.black};
  }
`;
const DesktopPrimaryNavButton = styled.button `
  ${ButtonReset};
  ${desktopTopLevelNavItemStyles};
`;
const DesktopPrimaryNavLink = styled.a `
  ${desktopTopLevelNavItemStyles};
`;
const DropdownMenu = styled.div `
  ${transition('visibility, opacity')};

  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 100%);
  height: calc(100vh - var(--headerHeightWithTopBar));
  width: 100vw;
  background-color: ${rgba(brand.global.black, 0.8)};
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  visibility: hidden;
  opacity: 0;
  pointer-events: none;

  ${DesktopNavItem}[data-is-active='true'] & {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;

    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: 0;
      width: 100%;
      height: 11px;
      background-color: ${brand.global.white};
    }
  }
`;
const DropdownMenuWrapper = styled.div `
  position: relative;
  background-color: ${brand.global.white};
  z-index: 1;
`;
const DropdownMenuInner = styled.div `
  ${siteWide};

  padding-top: 40px;
  padding-bottom: 64px;
`;
const DropdownMenuInnerTop = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;
const DropdownMenuTitle = styled.h2 `
  ${displayStyles};

  font-size: 32px;
  line-height: 32px;
`;
const DropdownMenuClose = styled.button `
  ${ButtonReset};

  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid ${brand.global.black};
  border-radius: ${borderRadius.round}px;
  color: ${brand.global.black};

  svg {
    width: 20px;
    height: 20px;
  }
`;
const DropdownMenuInnerBottom = styled.div `
  ${baseGrid};

  align-items: center;
`;
const CardsList = styled.div `
  display: grid;
  grid-template-columns: auto;
  column-gap: 32px;
  row-gap: ${fluid(24, 40)};
  margin-top: ${fluid(16, 32)};
  padding: 0 16px;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(2, 1fr);
    padding: 0;
  }
`;
const DropdownMenuInnerBottomContent = styled.div `
  grid-column: span 7;
`;
const DropdownMenuSummary = styled.div `
  ${contentStyles};

  max-width: 688px;
  margin-bottom: 32px;

  p {
    line-height: ${fluid(26, 30)};
  }
`;
const DropdownMenuNav = styled.ul `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: ${fluid(32, 64)};
  row-gap: ${fluid(16, 24)};

  @media ${until(Device.DesktopSmall)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const DropdownMenuNavLink = styled.a `
  ${linkOpposite};

  ${fonts.aptos.extraBold};

  display: flex;
  align-items: center;
  column-gap: 10px;

  svg {
    ${transition('color')};

    position: relative;
    flex-shrink: 0;
    width: 6px;
    height: 10px;
    top: -1px;
    color: ${brand.orange.base};
  }

  &:hover {
    svg {
      color: ${brand.tealAccessibleLight._1000};
    }
  }
`;
const DropdownMenuNavLinkText = styled.span `
  ${transition('transform')};

  ${DropdownMenuNavLink}:hover & {
    transform: translateX(4px);
  }
`;
const DropdownMenuImageWrapper = styled.div `
  grid-column: 9 / span 4;
`;
const DropdownMenuImage = styled.img `
  border-radius: ${fluid(borderRadius.xs, borderRadius.sm)};
`;
const DropdownMenuBackdrop = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  opacity: 0;
  z-index: 0;
`;
const MobileNav = styled.div `
  ${transition('transform, opacity, visibility')};
  /* display: none; */
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  transform: translateY(100%) translateY(10px);
  left: 0;
  width: 100vw;
  height: calc(100vh - var(--headerHeightWithTopBar));
  height: calc(100dvh - var(--headerHeightWithTopBar));
  overflow: visible;
  background-color: ${brand.global.white};
  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  &[data-is-open='true'] {
    /* display: flex;
    flex-direction: column; */
    opacity: 1;
    visibility: visible;
    transform: translateY(100%);
    pointer-events: auto;
  }

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 12px;
    min-height: 12px;
    background-color: ${brand.orange.base};
    border-top: 4px solid ${brand.red.base};
    box-sizing: border-box;
  }
`;
const MobileNavInner = styled.ul `
  padding-top: 8px;
`;
const MobileNavItem = styled.li ``;
const mobileNavItemStyles = css `
  ${fonts.aptos.extraBold};

  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 8px;
  width: 100%;
  height: 52px;
  color: ${brand.global.black};
  font-size: ${fluid(14, 20)};
  line-height: ${fluid(20, 26)};
  padding: 0 16px;

  svg {
    width: ${fluid(16, 24)};
    height: ${fluid(16, 24)};
    color: ${brand.orange.base};
  }
`;
const MobilePrimaryNavButton = styled.button `
  ${ButtonReset};

  ${mobileNavItemStyles};
`;
const MobilePrimaryNavLink = styled.a `
  ${mobileNavItemStyles};

  text-decoration: none;
`;
const MobileNavBottom = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-top: auto;
  padding: 24px 16px;
`;
const TrustPilotLogoMobileNavTray = styled(SVGTrustPilotMobileNavTray) `
  width: 128px;
  height: auto;
`;
const MobileNavSecondLevel = styled.div `
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${brand.global.white};
  overflow: auto;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 12px;
    min-height: 12px;
    background-color: ${brand.orange.base};
    border-top: 4px solid ${brand.red.base};
    box-sizing: border-box;
  }

  ${MobileNavItem}[data-is-active='true'] & {
    display: flex;
    flex-direction: column;
  }
`;
const MobileNavSecondLevelIntro = styled.div `
  padding: 24px 16px;
  background-color: ${brand.grey.grey96};

  p {
    ${fonts.aptos.regular};

    font-size: ${fluid(12, 16)};
    line-height: ${fluid(20, 24)};
    margin: 16px 0 0;
  }
`;
const MobileNavSecondLevelHeading = styled.button `
  ${ButtonReset};

  ${displayStyles};

  display: flex;
  align-items: center;
  column-gap: 16px;
  margin: 0;
  font-size: 20px;
  line-height: 20px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${brand.orange.base};
    transform: scaleX(-1);
  }
`;
const MobileNavSecondLevelSummary = styled.div `
  ${contentStyles};

  margin-top: 16px;
`;
const MobileNavSecondLevelNavLinkList = styled.ul `
  margin-top: 16px;
`;
const MobileNavSecondLevelNavLink = styled.a `
  ${mobileNavItemStyles};

  text-decoration: none;
`;
const MobileNavFeaturedImage = styled.div `
  margin-top: auto;
  padding: 24px 16px;

  ${DropdownMenuImage} {
    width: 100%;
    height: auto;
  }
`;
const HeaderStyles = {
    CallUs,
    CallUsLabel,
    CallUsLink,
    CallUsNumber,
    Container,
    DesktopNav,
    DesktopNavInner,
    DesktopNavItem,
    DesktopPrimaryNavButton,
    DesktopPrimaryNavLink,
    DropdownMenu,
    DropdownMenuBackdrop,
    DropdownMenuClose,
    DropdownMenuImage,
    DropdownMenuImageWrapper,
    DropdownMenuInner,
    DropdownMenuInnerBottom,
    DropdownMenuInnerBottomContent,
    DropdownMenuInnerTop,
    DropdownMenuNav,
    DropdownMenuNavLink,
    DropdownMenuNavLinkText,
    DropdownMenuSummary,
    DropdownMenuTitle,
    DropdownMenuWrapper,
    CardsList,
    HeaderCta,
    Inner,
    InnerRight,
    LogoAlt,
    LogoWrapper,
    MobileMenuToggle,
    MobileNav,
    MobileNavBottom,
    MobileNavFeaturedImage,
    MobileNavInner,
    MobileNavItem,
    MobilePrimaryNavButton,
    MobilePrimaryNavLink,
    MobileNavSecondLevel,
    MobileNavSecondLevelHeading,
    MobileNavSecondLevelIntro,
    MobileNavSecondLevelNavLink,
    MobileNavSecondLevelNavLinkList,
    MobileNavSecondLevelSummary,
    TopBarMobile,
    TrustPilotLogoMobileNavTray,
    TrustPilotLogoTopHeaderMobile,
};
export default HeaderStyles;
